import {Component, effect, inject, OnDestroy, OnInit, ViewContainerRef, signal, computed} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {AppAuthService} from '@services/core/auth/app-auth.service';
import {RegionService} from '@app/areas/auth/services/region.service';
import {ModalService} from '@services/helpers/modal/modal.service';
import {
  Dashboard,
  DashboardLayout,
} from '@models/app/dashboard-layouts/DashboardLayout';
import {TenantService} from '@services/core/tenant/tenant.service';
import {Subject} from 'rxjs/internal/Subject';
import {takeUntil} from 'rxjs/operators';
import {ThemeService} from '@services/core/theme/theme.service';

import {AlertComponent} from "@shared/components/alert/alert.component";
import {WebsocketService} from "@app/areas/real-time/services/websocket.service";
import {ChatAuthService} from "@app/areas/chat/services/chat-auth.service";
import {ChatApiService} from "@app/areas/chat/services/chat-api.service";
import {ChatService} from "@app/areas/chat/services/chat.service";
import {ConsolidatedNotificationsService} from "@app/areas/chat/services/consolidated-notifications.service";
import {ChatUserDirectoryService} from "@app/areas/chat/services/chat-user-directory.service";
import {DeviceIdService} from "@app/areas/device/services/device-id.service";
import {PractitionerUserData} from "@models/data/auth/AuthPractitionerUserData";
import { EnquiriesService } from './areas/crm/enquiries/services/enquiries.service'
import {UserOnboardingService} from "@app/areas/account/onboarding/services/user-onboarding.service";
import {
  MyOnboardingProgressComponent
} from "@app/areas/account/onboarding/components/my-onboarding-progress/my-onboarding-progress.component";
import { ImpersonationStateService } from '@app/areas/auth/services/impersonation-state.service';
import { SessionBootstrapService } from '@app/shared/services/core/session-bootstrap/session-bootstrap.service';
import { WorkspaceInviteRedemptionService } from '@app/shared/services/core/workspace-invitations/workspace-invite-redemption.service';
import { RevokedDeviceReEnrollModalComponent } from '@app/areas/security-devices/components/revoked-device-reenroll-modal/revoked-device-reenroll-modal.component';
import { KeyStoreService } from '@app/areas/chat/services/keystore.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    AlertComponent,
    MyOnboardingProgressComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly chatAuth = inject(ChatAuthService);
  private readonly keys = inject(KeyStoreService);
  private readonly router = inject(Router);
  private unsub = new Subject<void>();
  loading = true;

  userDashboard = {
    showClinician: false,
    showDataManagement: false,
  };

  onboarding = inject(UserOnboardingService);
  impersonation = inject(ImpersonationStateService);
  private readonly sessionBootstrap = inject(SessionBootstrapService);
  private readonly workspaceInviteRedemption = inject(WorkspaceInviteRedemptionService);
  readonly regionService = inject(RegionService);

  readonly homeRegion$ = computed(() => {
    const homeId = this.appAuthService.homeRegion$();
    if (!homeId) return null;
    return this.regionService.supportedRegions.find(r => r.id === homeId) ?? null;
  });

  readonly currentRegionDisplayName$ = computed(() => {
    const currentId = this.regionService.selectedRegion$();
    if (!currentId) return 'Unknown Region';
    return this.regionService.supportedRegions.find(r => r.id === currentId)?.displayName ?? currentId;
  });

  get supportedRegions() {
    return this.regionService.supportedRegions;
  }

  async switchRegionBack(): Promise<void> {
    const home = this.homeRegion$();
    if (home) {
      await this.regionService.selectRegion(home.id, { refreshPortal: true });
    }
  }

  async switchToSpecificRegion(regionId: string): Promise<void> {
    await this.regionService.selectRegion(regionId as any, { refreshPortal: true });
  }

  readonly currentPath$ = signal<string>('/');

  constructor(
    public modalService: ModalService,
    public appAuthService: AppAuthService,
    private vcr: ViewContainerRef,
    private tenantService: TenantService,
    private theme: ThemeService,
    private ws: WebsocketService,
    private chatApi: ChatApiService,
    private chat: ChatService,
    private chatNotifications: ConsolidatedNotificationsService,
    private chatDirectory: ChatUserDirectoryService,
    private device: DeviceIdService,
    private enquiries: EnquiriesService,
  ) {
    this.currentPath$.set((this.router.url ?? '').split('?')[0].split('#')[0]);

    this.router.events
      .pipe(takeUntil(this.unsub))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentPath$.set(event.urlAfterRedirects.split('?')[0].split('#')[0]);
        }
      });

    void this.workspaceInviteRedemption.state$();

    // subscribe to the user notification topic
    effect(() => {
      const ready = this.sessionBootstrap.ready$();
      const u = this.appAuthService.user$()
      const loading = this.shouldShowGlobalLoader(this.appAuthService.loading);
      const path = this.currentPath$();

      console.debug('[AppComponent] user effect', {
        userId: u?.identityProfile?.id ?? null,
        ready,
        loading,
        publicRoute: this.isPublicRoute,
        path,
        loggedIn: this.appAuthService.loggedIn,
      });

      if (u === null) {
        if (loading) {
          console.debug('[AppComponent] user effect waiting for auth/bootstrap', {
            path,
          });
          return;
        }
        this.handleUserNotLoggedIn();
        this.impersonation.state$.set({ active: false });
        return;
      }

      if (!ready) {
        return;
      }

      const userId = u?.identityProfile?.id ?? null;
      const onboardingStatus = this.onboarding.status$();
      const onboardingLoading = this.onboarding.loading$();

      if (onboardingStatus !== null && !onboardingLoading) {
        const requiresOnboarding = this.onboarding.requiresOnboarding$();
        const isAdmin = this.appAuthService.isAdmin;

        if (path === '/account/login' && this.appAuthService.loggedIn) {
          if (!isAdmin && requiresOnboarding) {
            this.navigateToOnboarding();
          } else {
            const returnUrl = this.getReturnUrl();
            if (returnUrl) {
              void this.router.navigateByUrl(returnUrl, { replaceUrl: true });
            } else {
              this.navigateToDashboard();
            }
          }
        } else if (!isAdmin && requiresOnboarding && !this.isPublicRoute && !path.startsWith('/onboarding')) {
          this.navigateToOnboarding();
        } else if (path.startsWith('/onboarding') && (isAdmin || !requiresOnboarding)) {
          this.navigateToDashboard();
        }
      }

      if (this.initialisedUser === userId) {
        return;
      }

      console.debug('[AppComponent] user changed', { userId, path });
      this.handleUserChanged(u)
      this.impersonation.syncState();
    })

    effect(() => {
      const loading = this.shouldShowGlobalLoader(this.appAuthService.loading);
      console.debug('[AppComponent] loader effect', {
        loading,
        authLoading: this.appAuthService.loading,
        bootstrapping: this.sessionBootstrap.bootstrapping$(),
        publicRoute: this.isPublicRoute,
      });
      this.showHideLoader(loading);
      this.setupDefaultTenant(loading);
      setTimeout(() => {
        this.loading = loading;
      });
    });

    // subscribing to the theme change after the DOM has been loaded in
    this.theme.theme$
      .pipe(takeUntil(this.unsub))
      .subscribe(t => {
        switch (t) {
          case 'dark':
            document.documentElement.classList.add('dark');
            break;
          case 'light':
            document.documentElement.classList.remove('dark');
            break;
        }
      })

    // Loading subscription
    this.appAuthService.loading$
      .pipe(takeUntil(this.unsub))
      .subscribe((loading) => this.handleUserLoading(loading));
  }

  ngOnInit(): void {
    this.setModalContainerReference();
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  get isPublicRoute(): boolean {
    const path = this.currentPath$();

      return (
        path.startsWith('/account') ||
        path.startsWith('/secure-document-upload') ||
        path.startsWith('/consultation-invite') ||
        path.startsWith('/workspace-invite')
      );
  }

  get shouldShowOnboardingProgress(): boolean {
    const path = this.currentPath$();

    return this.appAuthService.loggedIn$()
      && this.onboarding.status$() !== null
      && this.onboarding.requiresOnboarding$()
      && !this.appAuthService.isAdmin
      && !this.appAuthService.isPatientOnly
      && !path.startsWith('/onboarding');
  }

  private handleUserLoading(loading: boolean): void {
    const shouldLoad = this.shouldShowGlobalLoader(loading);
    console.debug('[AppComponent] handleUserLoading', {
      authLoading: loading,
      bootstrapping: this.sessionBootstrap.bootstrapping$(),
      shouldLoad,
      publicRoute: this.isPublicRoute,
    });
    this.showHideLoader(shouldLoad);
    this.setupDefaultTenant(shouldLoad);
    setTimeout(() => {
      this.loading = shouldLoad;
    });
  }

  private initialisedUser : string | null = null

  private handleUserChanged(u: PractitionerUserData): void {
    const deviceId = this.device.get();
    const userId = u?.identityProfile?.id ?? '';

    // do not re-initialise the same user
    if (this.initialisedUser === userId) {
      return;
    }

    // Only reset chat session when switching between different authenticated users
    if (this.initialisedUser && this.initialisedUser !== userId) {
      this.resetChatSession();
    }

    this.initialisedUser = userId;
    this.enquiries.refresh();

    // rotate the user key
    this.chatAuth.ensureDeviceKeyOnServer(this.chatApi, deviceId)
      .then()
      .catch(err => {
        const msg = String(err?.error?.error?.message || err?.message || err || '');
        if (msg.includes('DEVICE_REVOKED')) {
          console.warn('[AppComponent] Current device session is revoked');
          localStorage.removeItem('x25519-priv');
          localStorage.removeItem('x25519-pub');
          this.keys.clearConversationKeys();
          this.chat.clear();
          this.modalService.open(
            RevokedDeviceReEnrollModalComponent,
            {
              deviceId,
              platform: 'web',
              model: 'Current Browser',
            },
            true,
          );
        }
      });

    void this.onboarding.refresh();
  }

  private handleUserNotLoggedIn(): void {
    this.resetChatSession();
    this.ws.close()
    this.initialisedUser = null;
  }

  private resetChatSession(): void {
    this.chat.resetSession();
    this.chatAuth.resetSession();
    this.chatNotifications.resetSession();
    this.chatDirectory.clear();
  }

  private navigateToDashboard(): void {
    this.router.navigate([this.appAuthService.isPatientOnly ? '/cn' : '/cn/patient-list']).then();
  }

  private getReturnUrl(): string | null {
    const currentUrl = this.router.url ?? '';
    const queryIndex = currentUrl.indexOf('?');
    if (queryIndex < 0) {
      return null;
    }

    const queryPart = currentUrl.slice(queryIndex + 1).split('#')[0];
    const params = new URLSearchParams(queryPart);
    const returnUrl = params.get('returnUrl')?.trim() ?? '';

    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    return returnUrl;
  }

  private navigateToOnboarding(): void {
    this.router.navigate(['/onboarding']).then();
  }

  private setModalContainerReference(): void {
    this.modalService.setVcr = this.vcr;
  }

  private showHideLoader(loading): void {
    const element = document.getElementById('pageLoader');
    console.debug('[AppComponent] showHideLoader', {
      loading,
      hasElement: !!element,
    });
    if (loading) {
      element.style.display = 'block'
      // element.setAttribute('style', 'display:block !important');
    } else {
      element.style.display = 'none'
      // element.setAttribute('style', 'display:none !important');
    }
  }

  private setupDefaultTenant(isLoadingAuth: boolean): void {
    if (!isLoadingAuth && this.appAuthService.loggedIn) {
      this.tenantService.init();
    }
  }

  private shouldShowGlobalLoader(authLoading: boolean): boolean {
    if (this.appAuthService.isRegionNotSupported$()) {
      return false;
    }

    if (this.appAuthService.hasCachedAuthenticatedUser$()) {
      return false;
    }

    if (this.isPublicRoute && !this.appAuthService.loggedIn) {
      return authLoading;
    }

    if (!this.appAuthService.loggedIn && !authLoading) {
      return false;
    }

    const ready = this.sessionBootstrap.ready$();
    if (!ready) {
      return true;
    }

    if (
      this.appAuthService.loggedIn &&
      !this.appAuthService.isAdmin &&
      !this.appAuthService.isPatientOnly &&
      this.onboarding.status$() === null
    ) {
      return true;
    }

    return authLoading || this.sessionBootstrap.bootstrapping$();
  }

  private setupSelectedDashboard(selectedDashboard: DashboardLayout): void {
    this.userDashboard = {
      showClinician: (selectedDashboard?.type ?? null) === Dashboard.Clinician,
      showDataManagement:
        (selectedDashboard?.type ?? null) === Dashboard.DataManagement,
    };
  }
}
